import type { Country } from '@seek/content-safety-validator';
import { Box, IconClear, Textarea } from 'braid-design-system';
import React, {
  type ChangeEvent,
  type FocusEvent,
  type MouseEvent,
} from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { filterContent } from 'src/app/utils/validation';
import { CharacterCounter } from 'src/app/v1/views/shared/CharacterCounter';
import type { AppState } from 'src/types/appState';

import * as styles from './index.css';

export interface ItemType {
  id: string;
  isEditing: boolean;
  value: string;
}

interface ItemProps {
  handleEditItem: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  handleOnBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void;
  handleOnFocus?: (event: FocusEvent<HTMLTextAreaElement>) => void;
  handleRemoveItem?: (event: MouseEvent<HTMLButtonElement>) => void;
  handleOnPaste?: () => void;
  item: ItemType;
  maxLength?: number;
}

export const Item = ({
  handleEditItem,
  handleOnBlur,
  handleOnFocus,
  handleOnPaste,
  handleRemoveItem,
  item,
  maxLength = 300,
}: ItemProps) => {
  const { jobLocationCountry, hirerNation } = useSelector(
    (state: AppState) => ({
      jobLocationCountry: state.session.jobLocationCountry,
      hirerNation: state.session?.hirerAccount?.nation,
    }),
    shallowEqual,
  );
  const country = (
    jobLocationCountry ??
    hirerNation ??
    'AU'
  ).toLowerCase() as Country;
  const isEditMode = item.isEditing;
  const isBadWordsFound = !filterContent.test({
    value: item.value ?? '',
    validatorProps: {
      country,
    },
    formValues: {},
  });
  const isInvalid =
    Boolean(item.value) && (item.value.length > maxLength || isBadWordsFound);
  return (
    <Box display="flex" alignItems="center" data-cy="textInputListItem">
      <Box className={styles.dot} marginRight="xsmall" />
      <Box position="relative" flexGrow={1} marginRight="xsmall">
        <Textarea
          grow={false}
          id={item.id}
          aria-label={item.id}
          lines={1}
          name={item.id}
          onBlur={handleOnBlur}
          onChange={handleEditItem}
          onFocus={handleOnFocus}
          onPaste={handleOnPaste}
          tone={isInvalid ? 'critical' : 'neutral'}
          value={item.value}
        />

        {(isEditMode || isInvalid) && (
          <CharacterCounter maxLength={maxLength} value={item.value} />
        )}
      </Box>
      <Box
        component="button"
        type="button"
        id={`button-${item.id}`}
        name={item.id}
        onClick={handleRemoveItem}
        overflow="visible"
        cursor="pointer"
      >
        <IconClear size="xsmall" />
      </Box>
    </Box>
  );
};
